import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

interface ArrowLinkProps {
    href: string
    label: string
    direction?: 'right' | 'left'
    className?: string
}

interface ArrowIconProps {
    direction: 'right' | 'left'
}

// Hover and focus: the arrow nudges a few pixels and an underline draws in. Both are transforms only.
const ArrowIcon: FC<ArrowIconProps> = ({ direction }): ReactNode => {
    return (
        <svg
            aria-hidden='true'
            viewBox='0 0 26 26'
            className={cn(
                'size-6 shrink-0 transition-transform duration-fast ease-studio',
                direction === 'left'
                    ? 'rotate-180 group-hover/arrow:-translate-x-1 group-focus-visible/arrow:-translate-x-1'
                    : 'group-hover/arrow:translate-x-1 group-focus-visible/arrow:translate-x-1'
            )}
        >
            <path
                d='M24 4.904 L18.3 9.809 L18.3 5.7 L0 5.7 L0 4.109 L18.3 4.109 L18.3 0Z'
                transform='translate(1 9)'
                fill='currentColor'
            />
        </svg>
    )
}

const Underline: FC = (): ReactNode => (
    <span
        aria-hidden='true'
        className='absolute inset-x-0 bottom-1 h-px origin-left scale-x-0 bg-foreground transition-transform duration-base ease-studio group-hover/arrow:scale-x-100 group-focus-visible/arrow:scale-x-100'
    />
)

interface ArrowCueProps {
    label: string
    className?: string
}

export const ArrowCue: FC<ArrowCueProps> = ({ label, className }): ReactNode => {
    return (
        <span className={cn('relative inline-flex items-center gap-2 px-[2px] py-2 text-foreground', className)}>
            <span className='t-link whitespace-pre text-left'>
                {label}
            </span>

            <ArrowIcon direction='right' />
            <Underline />
        </span>
    )
}

export const ArrowLink: FC<ArrowLinkProps> = ({
    href,
    label,
    direction = 'right',
    className
}): ReactNode => {
    return (
        <Link
            href={href}
            className={cn('group/arrow relative inline-flex items-center gap-2 px-[2px] py-2 text-foreground', className)}
        >
            {direction === 'left' && <ArrowIcon direction='left' />}

            <span className='t-link whitespace-pre text-left'>
                {label}
            </span>

            {direction === 'right' && <ArrowIcon direction='right' />}

            <Underline />
        </Link>
    )
}
